import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AuditAction } from '../../lib/api/audits';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { ListPager, ListSearch, useListControls } from '../../components/common/listControls';
import { SortableTh } from '../../components/common/SortableTh';
import { UploadTableEmpty, UploadTableSkeleton } from '../barratt/upload-history/UploadTableStates';
import { formatDay } from '../barratt/uploadCycle';
import { CheckCircle2, Download, Pencil } from 'lucide-react';
import * as XLSX from 'xlsx';
import { ActionPriorityBadge, ActionStatusBadge } from './auditStatus';

type StatusFilter = 'open' | 'overdue' | 'completed' | 'all';

interface ActionsTableProps {
  actions: AuditAction[];
  loading: boolean;
  readOnly: boolean;
  /** Hidden inside an audit's detail page, where the audit column is redundant. */
  showAudit?: boolean;
  /** Compact: no status tabs or export (inside the audit detail). */
  compact?: boolean;
  onRaise?: () => void;
  onEdit?: (action: AuditAction) => void;
  onComplete?: (action: AuditAction) => void;
}

const OPEN = new Set(['open', 'in_progress', 'overdue']);

/** Actions list — in-memory search, sort and paging. Customer users edit and close; suppliers read. */
export default function ActionsTable({
  actions, loading, readOnly, showAudit = true, compact = false, onRaise, onEdit, onComplete,
}: ActionsTableProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(compact ? 'all' : 'open');
  const columns = 7 + (showAudit ? 1 : 0) + (readOnly ? 0 : 1);

  const filtered = useMemo(() => actions.filter((a) => {
    if (statusFilter === 'open' && !OPEN.has(a.status)) return false;
    if (statusFilter === 'overdue' && a.status !== 'overdue' && !(OPEN.has(a.status) && a.days_overdue > 0)) return false;
    if (statusFilter === 'completed' && a.status !== 'completed') return false;
    return true;
  }), [actions, statusFilter]);

  const list = useListControls(filtered, {
    searchText: (a) => [a.title, a.supplier_name, a.owner_name, a.audit_reference ?? ''].join(' '),
    sorters: {
      due_date: (a) => a.due_date,
      title: (a) => a.title.toLowerCase(),
      supplier: (a) => a.supplier_name.toLowerCase(),
      owner: (a) => a.owner_name.toLowerCase(),
      priority: (a) => ({ high: 0, medium: 1, low: 2 })[a.priority],
      status: (a) => a.status,
    },
    defaultSort: { key: 'due_date', dir: 'asc' },
    tieBreak: (a) => a.title,
    resetKeys: [statusFilter],
  });

  const counts = useMemo(() => ({
    open: actions.filter((a) => OPEN.has(a.status)).length,
    overdue: actions.filter((a) => OPEN.has(a.status) && a.days_overdue > 0).length,
    completed: actions.filter((a) => a.status === 'completed').length,
    all: actions.length,
  }), [actions]);

  const exportExcel = () => {
    const rows = list.orderedRows.map((a) => [
      a.title, a.supplier_name, a.audit_reference ?? '', a.owner_name, a.due_date, a.priority_display,
      a.status_display, a.days_overdue, a.completed_at ?? '', a.completion_notes,
    ]);
    const sheet = XLSX.utils.aoa_to_sheet([
      ['Action', 'Supplier', 'Audit', 'Owner', 'Due', 'Priority', 'Status', 'Days overdue', 'Completed', 'Notes'],
      ...rows,
    ]);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Actions');
    XLSX.writeFile(book, `actions${statusFilter !== 'all' ? `-${statusFilter}` : ''}.xlsx`);
  };

  const emptyMessage = actions.length === 0
    ? (readOnly ? 'No actions have been raised for your company.' : 'No actions raised yet.')
    : list.search ? `No actions match "${list.search}".` : 'No actions in this view.';

  return (
    <div className="space-y-4">
      {!compact && (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <TabsList data-testid="actions-status-filter">
              <TabsTrigger value="open">Open ({counts.open})</TabsTrigger>
              <TabsTrigger value="overdue">Overdue ({counts.overdue})</TabsTrigger>
              <TabsTrigger value="completed">Completed ({counts.completed})</TabsTrigger>
              <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <div className="md:w-72">
              <ListSearch value={list.search} onChange={list.setSearch} placeholder="Search action, supplier, owner, audit…" testId="actions-search" />
            </div>
            <Button variant="outline" size="sm" onClick={exportExcel} disabled={list.orderedRows.length === 0} data-testid="actions-export">
              <Download className="mr-2 h-4 w-4" />Export
            </Button>
            {!readOnly && onRaise && <Button size="sm" onClick={onRaise} data-testid="actions-raise">Raise action</Button>}
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {!compact && <ListPager list={list} position="top" noun="actions" testIdPrefix="actions" />}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTh field="title" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Action</SortableTh>
                  <SortableTh field="supplier" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Supplier</SortableTh>
                  {showAudit && <TableHead>Audit</TableHead>}
                  <SortableTh field="owner" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Owner</SortableTh>
                  <SortableTh field="due_date" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Due</SortableTh>
                  <SortableTh field="priority" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Priority</SortableTh>
                  <SortableTh field="status" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Status</SortableTh>
                  {!readOnly && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              {loading ? (
                <UploadTableSkeleton columns={columns} />
              ) : list.pageRows.length === 0 ? (
                <UploadTableEmpty columns={columns} message={emptyMessage} />
              ) : (
                <TableBody>
                  {list.pageRows.map((a) => {
                    const late = OPEN.has(a.status) && a.days_overdue > 0;
                    return (
                      <TableRow key={a.id} className={late ? 'border-l-4 border-l-red-500' : ''} data-testid={`action-${a.id}`}>
                        <TableCell>
                          <div className="font-medium text-foreground">{a.title}</div>
                          {a.description && <div className="line-clamp-1 text-xs text-muted-foreground">{a.description}</div>}
                        </TableCell>
                        <TableCell>{a.supplier_name}</TableCell>
                        {showAudit && (
                          <TableCell>
                            {a.audit ? <Link to={`/audits/${a.audit}`} className="text-primary hover:underline">{a.audit_reference}</Link>
                              : <span className="text-muted-foreground">Standalone</span>}
                          </TableCell>
                        )}
                        <TableCell>{a.owner_name}</TableCell>
                        <TableCell>
                          <div>{formatDay(a.due_date)}</div>
                          {late && <div className="text-xs text-red-600 dark:text-red-400">{a.days_overdue} day{a.days_overdue === 1 ? '' : 's'} overdue</div>}
                        </TableCell>
                        <TableCell><ActionPriorityBadge priority={a.priority} /></TableCell>
                        <TableCell><ActionStatusBadge status={a.status} /></TableCell>
                        {!readOnly && (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {onEdit && (
                                <Button variant="ghost" size="sm" onClick={() => onEdit(a)} aria-label={`Edit ${a.title}`} data-testid={`action-edit-${a.id}`}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              )}
                              {onComplete && OPEN.has(a.status) && (
                                <Button variant="outline" size="sm" onClick={() => onComplete(a)} data-testid={`action-complete-${a.id}`}>
                                  <CheckCircle2 className="mr-1 h-4 w-4" />Complete
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>
          </div>
          {!compact && <ListPager list={list} position="bottom" noun="actions" testIdPrefix="actions" />}
        </CardContent>
      </Card>
    </div>
  );
}
